import * as React from "react";
import { Text, View } from "react-native";
import { Picker } from "@react-native-picker/picker";
import { cva, VariantProps } from "class-variance-authority";
import { ChevronDownIcon } from "lucide-react-native";

import { cn } from "~/lib/utils";

interface PickerProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Picker>, "children">,
    VariantProps<typeof textInputStyles> {
  label?: string;
  hint?: string;
  errorMessage?: string;
  required?: boolean;
  options: React.ComponentPropsWithoutRef<typeof Picker.Item>[];
  value?: string;
  disabled?: boolean;
  wrapperClassName?: string;
}

export const SelectInput = React.forwardRef<
  React.ElementRef<typeof Picker>,
  PickerProps
>((props, forwardedRef) => {
  const inputRef = React.useRef<Picker<unknown>>(null);
  React.useImperativeHandle(forwardedRef, () => inputRef.current!);
  const {
    options,
    id,
    label,
    hint,
    errorMessage,
    required,
    size,
    value,
    disabled = false,
    wrapperClassName,
    ...rest
  } = props;
  const reactId = React.useId();
  const fieldId = id || reactId;
  const labelId = `${fieldId}-label`;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  return (
    <View className={cn("gap-2", wrapperClassName)}>
      {(label || required) && (
        <View className="flex-row items-center gap-x-2" id={labelId}>
          {label && (
            <Text
              className="text-input-label text-base font-semibold"
              onPress={() => inputRef.current?.focus()}
            >
              {label}
            </Text>
          )}
          {required && (
            <Text className="text-text-tertiary text-base font-semibold">
              (required)
            </Text>
          )}
        </View>
      )}
      {hint && (
        <Text
          id={hintId}
          onPress={() => inputRef.current?.focus()}
          className="text-text-tertiary text-base font-semibold"
        >
          {hint}
        </Text>
      )}
      <View className="relative">
        <Picker
          className={cn("appearance-none", textInputStyles({ size }))}
          ref={inputRef}
          id={fieldId}
          aria-labelledby={labelId}
          aria-describedby={hintId}
          aria-invalid={!!errorMessage}
          aria-errormessage={errorId}
          selectedValue={value}
          enabled={!disabled}
          {...rest}
        >
          {options.map(({ label, value, ...rest }) => (
            <Picker.Item key={value} label={label} value={value} {...rest} />
          ))}
        </Picker>
        <View className="absolute right-4 top-1/2 -translate-y-1/2 transform">
          <ChevronDownIcon className="size-4" />
        </View>
      </View>
      {errorMessage && (
        <Text id={errorId} className="text-red-500">
          {errorMessage}
        </Text>
      )}
    </View>
  );
});

const textInputStyles = cva(
  "border-input-border bg-transparent focus:bg-input-background-active focus:border-input-border-active text-text-primary rounded-lg border-2 outline-none",
  {
    variants: {
      size: {
        medium: "px-4 py-3 h-12",
        small: "px-4 py-2 h-9",
      },
    },
    defaultVariants: {
      size: "medium",
    },
  },
);
